import { useEffect, useState, type CSSProperties } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dices, RotateCcw, Sparkles, X } from "lucide-react";
import { RECOVERY_BOARD_SPACES as SPACES, SIDE_META, type Side } from "@/data/recovery-board";
import { SPACE_DETAILS } from "@/data/recovery-board-details";
import { useRecoveryBoardProgress } from "@/hooks/useRecoveryBoardProgress";
import { Button } from "@/components/ui/button";
import { useBoardJournal } from "@/hooks/useBoardJournal";
import { playDiceRattle } from "@/lib/dice-sound";
import { BoardJourney } from "@/components/recovery-board/BoardJourney";
import { toDeckCard, type BoardCardRow } from "@/data/board-card-row";
import { DECKS, DRAW_SPACES, levelFor, type DeckCard, type DeckId } from "@/data/recovery-board-cards";
import { DrawCard } from "@/components/recovery-board/DrawCard";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { Die } from "@/components/recovery-board/Dice";
import type { RecoveryBoardProgress } from "@/hooks/useRecoveryBoardProgress";
import deckReflect from "@/assets/recovery-board/back-reflection.jpg";
import deckSurprise from "@/assets/recovery-board/back-surprise.jpg";
import { GuidedBreathingTimer } from "@/components/GuidedBreathingTimer";

import { Link } from "@tanstack/react-router";
import { MEMBERSHIP_JOURNEY_PATH } from "@/lib/funnel";

// Perimeter of a 7×7 grid, starting bottom-right and moving clockwise around
// the board (Monopoly style): bottom row right→left, left column up, top row
// left→right, right column down.
function cellFor(i: number): { row: number; col: number } {
  if (i <= 6) return { row: 7, col: 7 - i };
  if (i <= 12) return { row: 7 - (i - 6), col: 1 };
  if (i <= 18) return { row: 1, col: 1 + (i - 12) };
  return { row: 1 + (i - 18), col: 7 };
}

// Border colours only — the card body itself must stay solid (bg-card) so the
// board never shows through behind the text, in day or night mode.
const SIDE_STYLE: Record<Side, string> = {
  corner: "border-[var(--gold)]",
  flight: "border-[var(--rb-flight)]",
  fight: "border-[var(--rb-fight)]",
  freeze: "border-[var(--rb-freeze)]",
  fawn: "border-[var(--rb-fawn)]",
};

const STRIP: Record<Side, string> = {
  corner: "var(--rb-corner)",
  flight: "var(--rb-flight)",
  fight: "var(--rb-fight)",
  freeze: "var(--rb-freeze)",
  fawn: "var(--rb-fawn)",
};

const seasonStyle = (side: Side) =>
  ({ ["--season" as string]: STRIP[side] }) as CSSProperties;

// Two Monopoly-style piles: each holds two of the four card categories.
const PILES = [
  { id: "calm", name: "Reflect", img: deckReflect, decks: ["reflection", "grounding"] as DeckId[] },
  { id: "joy", name: "Surprise", img: deckSurprise, decks: ["surprise", "mystery"] as DeckId[] },
] as const;
const pileFor = (d: DeckId) => (d === "reflection" || d === "grounding" ? 0 : 1);


export function RecoveryBoard({ embedded = false, guest = false }: { embedded?: boolean; guest?: boolean } = {}) {
  const state = useRecoveryBoardProgress();
  const [movingPos, setMovingPos] = useState<number | null>(null);
  const [rolling, setRolling] = useState(false);
  const [die, setDie] = useState<number | null>(null);
  const [card, setCard] = useState<number | null>(null);

  const journal = useBoardJournal();
  const [liveCards, setLiveCards] = useState<BoardCardRow[]>([]);
  useEffect(() => {
    supabase.from("board_cards").select("*").eq("active", true).order("sort_order")
      .then(({ data }) => { if (data) setLiveCards(data as BoardCardRow[]); });
  }, []);
  const [draw, setDraw] = useState<{ deck: DeckId; card: DeckCard } | null>(null);
  const [pending, setPending] = useState<DeckId | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // Visitors on the homepage can roll the dice once, then are invited to join.
  const [joinAsk, setJoinAsk] = useState(false);
  const guestRoll = () => {
    if (rolling) return;
    playDiceRattle();
    setRolling(true);
    let ticks = 0;
    const t = window.setInterval(() => {
      setDie(1 + Math.floor(Math.random() * 6));
      if (++ticks >= 8) { window.clearInterval(t); setRolling(false); setJoinAsk(true); }
    }, 70);
  };

  const showFlash = (msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 1800);
  };

  const visit = (p: RecoveryBoardProgress, pos: number): RecoveryBoardProgress => ({
    ...p, pos, visited: p.visited.includes(pos) ? p.visited : [...p.visited, pos],
  });

  // After landing, the space card (or a drawn deck card) opens automatically.
  // Every card has clear close buttons, so the dice is free again once it's closed.
  const land = (p: RecoveryBoardProgress) => {
    state.saveProgress(p);
    journal.record({ kind: "space", space_index: p.pos, space_title: SPACES[p.pos].title, deck: null, card_text: null, outcome: "landed", tokens_after: p.tokens });
    const deck = DRAW_SPACES[p.pos];
    window.setTimeout(() => {
      if (deck) {
        const live = liveCards.filter((c) => c.deck === deck);
        const cards: DeckCard[] = live.length ? live.map(toDeckCard) : DECKS[deck].cards;
        setPending(null);
        setDraw({ deck, card: cards[Math.floor(Math.random() * cards.length)] });
      } else setCard(p.pos);
    }, 600);
  };

  const drawFrom = (pile: number) => {
    if (!pending || pileFor(pending) !== pile) return;
    const live = liveCards.filter((c) => c.deck === pending);
    const cards: DeckCard[] = live.length ? live.map(toDeckCard) : DECKS[pending].cards;
    setDraw({ deck: pending, card: cards[Math.floor(Math.random() * cards.length)] });
    setPending(null);
  };

  const roll = (base?: RecoveryBoardProgress) => {
    if (guest) return guestRoll();
    if (rolling || pending || state.loading || state.error) return;
    const start = base ?? { pos: state.pos, tokens: state.tokens, visited: state.visited };
    playDiceRattle();
    setRolling(true);
    setPending(null);
    setCard(null);
    let ticks = 0;
    const t = window.setInterval(() => {
      setDie(1 + Math.floor(Math.random() * 6));
      if (++ticks >= 8) {
        window.clearInterval(t);
        const n = 1 + Math.floor(Math.random() * 6);
        setDie(n);
        let step = 0;
        const mover = window.setInterval(() => {
          step++;
          const pos = (start.pos + step) % SPACES.length;
          setMovingPos(pos);
          if (step >= n) {
            window.clearInterval(mover);
            setMovingPos(null);
            setRolling(false);
            showFlash("+1 🌟");
            land(visit({ ...start, tokens: start.tokens + 1 }, pos));
          }
        }, 220);
      }
    }, 70);
  };

  const finishDraw = (accepted: boolean) => {
    if (!draw) return;
    const { deck, card: c } = draw;
    setDraw(null);
    const base = { pos: state.pos, tokens: state.tokens, visited: state.visited };
    const reward = deck === "reflection" ? 1 : c.reward ?? 0;
    journal.record({ kind: "card", space_index: base.pos, space_title: SPACES[base.pos].title, deck, card_text: c.text, outcome: accepted ? "accepted" : "skipped", tokens_after: base.tokens + (accepted ? reward : 0) });
    if (!accepted) { showFlash("Skipped gently 💛"); return; }
    const p = { ...base, tokens: base.tokens + reward };
    if (reward) showFlash(`+${reward} 🌟`);
    if (c.effect?.move) {
      const pos = (base.pos + c.effect.move) % SPACES.length;
      const moved = visit(p, pos);
      state.saveProgress(moved);
      window.setTimeout(() => setCard(pos), 600);
    } else if (c.effect?.rollAgain) {
      state.saveProgress(p);
      window.setTimeout(() => roll(p), 400);
    } else if (reward) state.saveProgress(p);
  };

  const reset = () => {
    state.saveProgress({ pos: 0, tokens: 0, visited: [0] });
    setDie(null);
    setPending(null);
    setCard(null);
  };

  const currentPos = movingPos ?? state.pos;
  const current = SPACES[currentPos];
  const sp = card !== null ? SPACES[card] : null;
  const lv = levelFor(state.tokens);

  const Wrap = embedded ? "section" : "main";
  const Heading = embedded ? "h2" : "h1";
  // The homepage copy sits inside the page flow, so it plays a little smaller
  // than the full board page — the words were too big, especially on phones.
  const compact = embedded;
  return (
    <Wrap className={embedded ? "mx-auto flex w-full max-w-5xl flex-col items-center" : "mx-auto flex w-full max-w-5xl flex-col items-center px-2 pb-32 pt-1 sm:px-4 sm:pb-10 sm:pt-2"}>
      <header className="mb-1 text-center sm:mb-2">
        <Heading className={`font-serif text-foreground ${compact ? "text-base sm:text-2xl" : "text-xl sm:text-3xl"}`}>Recovery Board 🌿</Heading>
        <p className="mx-auto hidden max-w-xl text-sm font-medium text-foreground/85 sm:block">
          Roll, travel and draw cards. Every card is optional — no winning or losing.
        </p>
      </header>

      <div className="mb-1 flex w-full max-w-md flex-wrap items-center justify-center gap-1">
        {(Object.keys(SIDE_META) as (keyof typeof SIDE_META)[]).map((k) => (
          <span
            key={k}
            style={seasonStyle(k)}
            className={`rt-rb-chip inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-semibold ${compact ? "text-[9px] sm:text-[10px]" : "text-[11px]"}`}
          >
            {SIDE_META[k].emoji} {SIDE_META[k].label}
          </span>
        ))}
      </div>



      <div
        style={{ minWidth: compact ? "14rem" : "17rem", gridTemplateColumns: "repeat(7, minmax(0,1fr))", gridTemplateRows: "repeat(7, minmax(0,1fr))", background: "var(--rb-felt)" }}
        className={`grid aspect-square rounded-2xl border-[var(--gold)] shadow-lg ${compact ? "w-[min(94%,calc(100dvh-13rem),26rem)] gap-[3px] border-[3px] p-0.5 sm:gap-1 sm:p-1" : "w-[min(100%,calc(100dvh-14rem),36rem)] gap-1 border-4 p-1 sm:gap-1.5 sm:p-2"}`}
      >
        {SPACES.map((s, i) => {
          const { row, col } = cellFor(i);
          const here = currentPos === i;
          const seen = state.visited.includes(i);
          const deck = DRAW_SPACES[i];
          return (
            <button
              key={i}
              type="button"
              onClick={() => (guest ? setJoinAsk(true) : setCard(i))}
              style={{ gridRow: row, gridColumn: col, borderTopColor: STRIP[s.side] }}
              aria-label={`${s.title}${here ? " — you are here" : ""}`}
              className={`relative flex flex-col items-center justify-center overflow-hidden rounded-md border bg-card p-0.5 text-center shadow-sm transition-transform hover:scale-[1.04] ${compact ? "border-t-[4px] sm:border-t-[6px]" : "border-t-[6px] sm:border-t-8"} ${s.side === "corner" ? "bg-[var(--qa-gold-tint)]" : ""} ${here ? "z-10 scale-110 ring-2 ring-[var(--gold-deep)]" : ""} ${seen ? "" : "opacity-90"}`}
            >
              <span className={`leading-none drop-shadow-sm ${compact ? "text-sm sm:text-xl" : "text-base sm:text-2xl"}`}>{s.emoji}</span>
              <span className={`mt-0.5 line-clamp-2 w-full font-medium leading-[1.1] tracking-[-0.03em] [overflow-wrap:normal] [word-break:normal] text-foreground ${compact ? "text-[7px] sm:text-[8px]" : "text-[8px] sm:text-[9px]"}`}>{compact ? s.short ?? s.title : s.title}</span>
              {deck && (
                <span aria-hidden className="absolute bottom-0 left-0 right-0 h-1" style={{ background: pileFor(deck) === 0 ? "var(--rb-flight)" : "var(--rb-corner)" }} />
              )}
              {seen && !here && <span aria-hidden className="absolute right-0.5 top-0 text-[7px] text-[var(--gold-deep)]">●</span>}
              {here && <span aria-hidden className="absolute -top-0.5 right-0 text-xs motion-safe:animate-bounce sm:text-base">🦋</span>}
            </button>
          );
        })}

        <div
          className={`relative flex min-w-0 flex-col items-center justify-between overflow-hidden rounded-xl border border-[var(--gold)]/50 bg-card/85 text-center backdrop-blur-sm ${compact ? "p-1 sm:p-2" : "p-1.5 sm:p-3"}`}
          style={{ gridRow: "2 / 7", gridColumn: "2 / 7" }}
        >
          <p className={`line-clamp-1 w-full font-serif leading-tight text-foreground ${compact ? "text-[10px] sm:text-sm" : "text-xs sm:text-lg"}`}>
            {current.emoji} {current.title}
          </p>

          <div className="flex w-full items-center justify-around gap-1">
            {PILES.map((pile, idx) => idx === 0 && (
              <Pile key={pile.id} pile={pile} compact={compact} active={!!pending && pileFor(pending) === idx} onDraw={() => drawFrom(idx)} />
            ))}
            <div className="flex flex-col items-center gap-1">
              <Die value={die ?? 1} size={compact ? "xs" : "sm"} rolling={rolling} />
              <span role="status" className="sr-only">
                {rolling ? "Rolling the dice…" : die ? `You rolled ${die}` : "The dice have not been rolled yet"}
              </span>
              <button
                type="button"
                onClick={() => roll()}
                disabled={guest ? rolling : rolling || state.loading || state.saving || state.error || !!pending}
                title={pending ? "Draw your card before rolling again" : undefined}
                className={`rt-rb-cta inline-flex items-center gap-1.5 rounded-full font-semibold shadow-md disabled:opacity-60 ${compact ? "min-h-9 px-2.5 text-[11px] sm:min-h-10 sm:px-3 sm:text-xs" : "min-h-10 px-3 text-xs sm:min-h-11 sm:px-4 sm:text-sm"}`}
              >
                <Dices className="h-4 w-4" />
                {pending ? "Draw your card" : rolling ? "Rolling…" : "Roll"}
              </button>
            </div>
            {PILES.map((pile, idx) => idx === 1 && (
              <Pile key={pile.id} pile={pile} compact={compact} active={!!pending && pileFor(pending) === idx} onDraw={() => drawFrom(idx)} />
            ))}
          </div>

          <div className="w-full max-w-[13rem]" aria-label={`Level: ${lv.level.name}`}>
            <p className={`flex items-center justify-center gap-1 font-bold leading-tight text-foreground ${compact ? "text-[10px] sm:text-xs" : "text-xs sm:text-sm"}`}>
              <Sparkles className="h-3 w-3 text-[var(--gold-deep)]" /> {state.tokens} recovery tokens · {lv.level.emoji} {lv.level.name}
            </p>
            <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-[var(--gold)] transition-all duration-700" style={{ width: `${lv.pct}%` }} />
            </div>
          </div>

          {joinAsk && (
            <div role="status" className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-1.5 rounded-xl bg-card p-2 text-center">
              <p className={`font-semibold text-foreground ${compact ? "text-xs sm:text-sm" : "text-sm sm:text-base"}`}>
                You rolled a {die}! 🎲 Become a member to move around the board and play.
              </p>
              <Link to={MEMBERSHIP_JOURNEY_PATH} className={`rt-rb-cta inline-flex items-center rounded-full font-semibold shadow ${compact ? "min-h-10 px-4 text-xs sm:text-sm" : "min-h-11 px-5 text-sm"}`}>Become a member</Link>
              <Link to="/login" className={`${compact ? "text-[11px]" : "text-xs"} font-medium text-foreground underline underline-offset-2`}>Already a member? Log In</Link>
            </div>
          )}
          {flash && (
            <p aria-live="polite" className={`pointer-events-none absolute inset-x-1 top-1/2 -translate-y-1/2 rounded-full bg-[var(--qa-gold-tint)] px-2 py-1 font-semibold text-[var(--gold-deep)] shadow motion-safe:animate-in motion-safe:zoom-in-90 motion-safe:fade-in ${compact ? "text-[10px] sm:text-xs" : "text-xs sm:text-sm"}`}>{flash}</p>
          )}
        </div>
      </div>

      {!guest && <div className={`mt-2 flex w-full max-w-md flex-wrap items-center justify-center gap-x-4 gap-y-1 font-medium text-foreground/85 ${compact ? "text-[10px] sm:text-xs" : "text-xs sm:text-sm"}`}>
        <span>{state.visited.length}/24 explored</span>
        <span role="status">
          {state.error ? "Your account save is unavailable." : state.loading ? "Loading your saved game…" : state.saving ? "Saving…" : "Saved ✓"}
        </span>
        {state.error && <Button variant="outline" size="sm" onClick={state.retrySave}>Try again</Button>}
        <button type="button" onClick={reset} disabled={rolling || state.loading || state.saving || state.error} className="inline-flex items-center gap-1 underline-offset-2 hover:underline">
          <RotateCcw className="h-3 w-3" /> Start again
        </button>
      </div>}
      <p className={`mx-auto mt-1 max-w-md text-center font-medium text-foreground/85 ${compact ? "text-[10px] leading-snug sm:text-xs" : "text-xs"}`}>
        Spaces are coloured by the trauma response they belong to. Green-edged
        spaces draw a Reflect card · gold-edged spaces a Surprise card.
      </p>

      {!embedded && <BoardJourney entries={journal.entries} failed={journal.failed} />}
      {embedded ? (
        !guest && <Button asChild size="sm" variant="outline" className="mt-3 text-xs"><Link to="/recovery-board">Open the full board & my journey</Link></Button>
      ) : (
        <Button asChild variant="outline" className="mt-3"><Link to="/daily-practice">Daily practice</Link></Button>
      )}
      {!embedded && <ComplianceNotice variant="board" className="mt-4 w-full max-w-md sm:max-w-2xl" />}


      {draw && <DrawCard key={draw.card.text} deck={draw.deck} card={draw.card} onDone={finishDraw} />}

      {sp && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={sp.title}
          className="fixed inset-0 z-[60] flex items-end justify-center bg-foreground/40 p-3 pb-28 sm:items-center sm:pb-3"
          onClick={() => setCard(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`relative max-h-[calc(100dvh-8rem)] w-full max-w-md overflow-y-auto rounded-2xl border-2 bg-card p-5 shadow-xl motion-safe:animate-in motion-safe:zoom-in-95 ${SIDE_STYLE[sp.side]}`}
          >
            <button type="button" onClick={() => setCard(null)} aria-label="Close card" className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground hover:bg-background/60">
              <X className="h-5 w-5" />
            </button>
            {sp.side !== "corner" && (
              <>
                <span
                  style={seasonStyle(sp.side)}
                  className="rt-rb-chip mt-1 inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                >
                  {SIDE_META[sp.side].emoji} {SIDE_META[sp.side].label}
                </span>
                <p className="mt-1 text-xs font-medium text-foreground/85">{SIDE_META[sp.side].blurb}</p>
              </>
            )}
            <div className="text-4xl">{sp.emoji}</div>
            <h2 className="mt-2 font-serif text-2xl text-foreground">{sp.title}</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground">{sp.learn}</p>
            {SPACE_DETAILS[sp.title]?.more.map((t, i) => (
              <p key={i} className="mt-2 text-sm leading-relaxed text-foreground">{t}</p>
            ))}
            {SPACE_DETAILS[sp.title] && (
              <div className="mt-4 rounded-xl border border-border bg-background/70 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gold-deep)]">💡 Helpful tips</p>
                <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-foreground">
                  {SPACE_DETAILS[sp.title].tips.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            )}
            <div className="mt-4 rounded-xl bg-background/70 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gold-deep)]">Try this</p>
              <p className="mt-1 text-sm text-foreground">{sp.try}</p>
            </div>
            <div className="mt-4"><GuidedBreathingTimer key={card} /></div>
            {card !== null && <CardActivity key={card} index={card} />}
            <div className="mt-5 flex flex-wrap gap-2">
              <Link to={sp.to as never} className="inline-flex items-center rounded-full border border-border bg-card px-4 py-2 text-sm text-foreground">
                More: {sp.cta}
              </Link>
              <button type="button" onClick={() => setCard(null)} className="rounded-full border border-border bg-card px-4 py-2 text-sm text-foreground">
                Keep playing
              </button>
            </div>
          </div>
        </div>
      )}
    </Wrap>
  );
}

const BREATHE = new Set(["Nervous System Regulation", "Meditation", "Rest Garden", "Tapping (EFT)", "Calm Forest", "Somatic Exercises"]);
const GROUND = new Set(["Emotional Flashbacks", "Grounding Tools"]);

function CardActivity({ index }: { index: number }) {
  const sp = SPACES[index];
  if (sp.title === "Support & Safety") return null;
  if (BREATHE.has(sp.title)) return null; // The shared guided timer appears on every space.
  if (GROUND.has(sp.title)) return <GroundActivity />;
  return <JournalActivity title={sp.title} prompt={sp.try} />;
}

const SENSES = ["5 things you can see", "4 things you can touch", "3 things you can hear", "2 things you can smell", "1 thing you can taste"];
function GroundActivity() {
  const [done, setDone] = useState<number[]>([]);
  return (
    <div className="mt-4 rounded-xl bg-background/70 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gold-deep)]">Do it here · 5-4-3-2-1</p>
      <ul className="mt-2 space-y-1.5">
        {SENSES.map((s, i) => (
          <li key={s}>
            <button type="button" aria-pressed={done.includes(i)} data-saved={done.includes(i) || undefined}
              onClick={() => setDone(done.includes(i) ? done.filter((d) => d !== i) : [...done, i])}
              className="rt-choice flex min-h-11 w-full items-center gap-2 rounded-lg border border-border bg-card px-3 text-left text-sm text-foreground">
              <span aria-hidden>{done.includes(i) ? "✓" : "○"}</span> {s}
            </button>
          </li>
        ))}
      </ul>
      {done.length === 5 && <p className="mt-2 text-sm text-foreground">You're here, right now. 💛</p>}
    </div>
  );
}

function JournalActivity({ title, prompt }: { title: string; prompt: string }) {
  const [text, setText] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const save = async () => {
    if (!text.trim()) return;
    setStatus("saving");
    const { data } = await supabase.auth.getUser();
    if (!data.user) return setStatus("error");
    const { error } = await supabase.from("journal_entries").insert({
      user_id: data.user.id, title: `Recovery Board · ${title}`, prompt, content: text.trim(),
    });
    setStatus(error ? "error" : "saved");
  };
  return (
    <div className="mt-4 rounded-xl bg-background/70 p-3">
      <label htmlFor="rb-journal" className="text-xs font-semibold uppercase tracking-wide text-[var(--gold-deep)]">Write here · private journal</label>
      <textarea id="rb-journal" value={text} disabled={status === "saved"} onChange={(e) => { setText(e.target.value); if (status === "error") setStatus("idle"); }}
        rows={3} placeholder="A few words are enough…"
        className="mt-2 w-full rounded-lg border border-border bg-card p-2 text-sm text-foreground" />
      <div className="mt-2 flex items-center gap-2">
        <button type="button" onClick={save} disabled={!text.trim() || status === "saving" || status === "saved"}
          className="rt-rb-cta rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-60">
          {status === "saving" ? "Saving…" : status === "saved" ? "Saved to your Journal ✓" : "Save to my Journal"}
        </button>
        {status === "error" && <span role="alert" className="text-xs text-destructive">Couldn't save — please try again.</span>}
      </div>
    </div>
  );
}

function Pile({ pile, compact, active, onDraw }: { pile: (typeof PILES)[number]; compact?: boolean; active: boolean; onDraw: () => void }) {
  return (
    <button
      type="button"
      onClick={onDraw}
      disabled={!active}
      aria-label={active ? `Draw a ${pile.name} card` : `${pile.name} cards — land on a matching space to draw`}
      className={`group relative flex flex-col items-center gap-0.5 transition-transform ${compact ? "w-[clamp(2rem,9vw,3.25rem)]" : "w-[clamp(2.6rem,13vw,4.5rem)]"} ${active ? "motion-safe:animate-bounce" : "opacity-85"}`}
    >
      <span className="relative block aspect-[5/7] w-full">
        <img src={pile.img} alt="" width={688} height={960} loading="lazy" className="absolute inset-0 h-full w-full translate-x-[3px] translate-y-[3px] rotate-6 rounded-md object-cover opacity-60 shadow" />
        <img src={pile.img} alt="" width={688} height={960} loading="lazy" className={`absolute inset-0 h-full w-full rounded-md object-cover shadow-md ${active ? "ring-2 ring-[var(--gold-deep)] ring-offset-1 ring-offset-card" : ""}`} />
      </span>
      <span className={`font-semibold leading-none ${compact ? "text-[9px] sm:text-[11px]" : "text-[11px] sm:text-sm"} ${active ? "text-[var(--gold-deep)]" : "text-foreground"}`}>
        {active ? "Tap to draw" : pile.name}
      </span>
    </button>
  );
}
